import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { ClipboardService } from 'ngx-clipboard';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { PhxInputClipboardProps } from './phx-input-clipboard-interfaces-types';

@Component({
  selector: 'app-phx-input-clipboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    InputGroupModule,
    ButtonModule,
    InputTextModule,
    TooltipModule
  ],
  templateUrl: './phx-input-clipboard.component.html',
  styleUrl: './phx-input-clipboard.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhxInputClipboardComponent {
  private clipboardService = inject(ClipboardService);
  private notificationService = inject(NotificationService);

  formControl = input.required<FormlyFieldConfig['formControl']>();
  props = input.required<PhxInputClipboardProps>();
  dataTestId = input.required<string>();

  /** Placeholder text for the input field */
  placeholder = input<string>();

  /** Whether the input is readonly */
  readonly = input<boolean>(false);

  /** Whether the input is required */
  required = input<boolean>(false);

  /** Whether the input should autofocus */
  autofocus = input<boolean>(false);

  /** Input type (defaults to 'text') */
  type = input<string>('text');

  /** Signal that tracks the current FormControl value for reactivity */
  private readonly formControlValue = signal<string | null | undefined>(null);

  constructor() {
    // Subscribe to FormControl valueChanges to update the signal reactively
    // This ensures the component detects programmatic value changes (e.g., setValue)
    effect(() => {
      const control = this.formControl();
      if (control) {
        // Set initial value
        this.formControlValue.set(control.value ?? null);

        // Subscribe to value changes
        const subscription = control.valueChanges.subscribe((value) => {
          this.formControlValue.set(value ?? null);
        });

        // Cleanup subscription when component is destroyed
        return () => subscription.unsubscribe();
      }
    });
  }

  /** Computed view model that merges props and inputs */
  readonly viewModel = computed(() => {
    return {
      formControl: this.formControl(),
      placeholder: this.props()?.placeholder ?? this.placeholder(),
      readonly: this.props()?.readonly ?? this.readonly(),
      required: this.props()?.required ?? this.required(),
      autofocus: this.props()?.autofocus ?? this.autofocus(),
      type: this.props()?.type ?? this.type(),
      dataTestId: this.props()?.attributes?.['data-testid'] ?? this.dataTestId(),
      hasValue: !!this.formControlValue()
    };
  });

  /**
   * Copies the current input value to the clipboard
   */
  copyToClipboard(): void {
    const value = this.formControlValue();

    if (!value) {
      return;
    }

    const result = this.clipboardService.copyFromContent(value);

    if (!result) {
      this.notificationService.warning(_('common.copied-to-clipboard-error'));
      return;
    }
    // Avoid spamming the user with success messages
    // this.notificationService.success(_('common.copied-to-clipboard-success'));
  }
}

